import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminInstructorList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const INSTRUCTOR_PAGE_SIZE = 30;
const instructorSortOptions = [
  { value: 'active-status', label: '稼働状態順' },
  { value: 'public-status', label: '公開状態順' },
  { value: 'name-asc', label: '講師名昇順' },
  { value: 'name-desc', label: '講師名降順' },
  { value: 'updated-desc', label: '更新日が新しい順' },
];

const instructorColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const memberColumnStyle = {
  width: '15rem',
  minWidth: '15rem',
};

const statusColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};
const specialtiesColumnStyle = {
  width: '15rem',
  minWidth: '15rem',
};
const updatedAtColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};
const actionColumnStyle = {
  width: '8.75rem',
  minWidth: '8.75rem',
  whiteSpace: 'nowrap',
};
const actionGroupStyle = {
  minWidth: '8.75rem',
  justifyContent: 'flex-start',
};

const compareText = (left = '', right = '') => String(left || '').localeCompare(String(right || ''), 'ja');

const comparableDateTime = (value = '') => {
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortInstructorRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'active-status') {
      if (left.isActive === right.isActive) {
        return compareText(left.instructorName, right.instructorName);
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'public-status') {
      if (left.publicProfileEnabled === right.publicProfileEnabled) {
        return compareText(left.instructorName, right.instructorName);
      }
      return left.publicProfileEnabled ? -1 : 1;
    }

    if (sortKey === 'name-desc') {
      return compareText(right.instructorName, left.instructorName);
    }

    if (sortKey === 'updated-desc') {
      return comparableDateTime(right.updatedAt) - comparableDateTime(left.updatedAt);
    }

    return compareText(left.instructorName, right.instructorName);
  });

  return nextRows;
};

const statusToUi = (isEnabled) => ({
  label: isEnabled ? '稼働中' : '停止中',
  tone: isEnabled ? 'published' : 'draft',
});

const publicStatusToUi = (isEnabled) => ({
  label: isEnabled ? '公開' : '非公開',
  tone: isEnabled ? 'published' : 'draft',
});

const AdminInstructorsListPage = () => {
  const content = yowanPageContent['/admin/events/instructors'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('active-status');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadInstructors = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminInstructorList({
          page: currentPage,
          pageSize: INSTRUCTOR_PAGE_SIZE,
          q: searchQuery.trim(),
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('講師情報を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadInstructors();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortInstructorRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / INSTRUCTOR_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * INSTRUCTOR_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref="/admin/events/instructors" routeKey="/admin/events/instructors">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="講師名 / メールアドレス / 会員 / 肩書き / 専門領域 / 稼働"
        sortOptions={instructorSortOptions}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={instructorColumnStyle}>講師</th>
              <th scope="col" style={memberColumnStyle}>紐づく会員</th>
              <th scope="col" style={specialtiesColumnStyle}>専門領域</th>
              <th scope="col" style={statusColumnStyle}>稼働</th>
              <th scope="col" style={statusColumnStyle}>公開</th>
              <th scope="col" style={updatedAtColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const activeUi = statusToUi(row.isActive);
              const publicUi = publicStatusToUi(row.publicProfileEnabled);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td style={instructorColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.instructorName}</p>
                    <p className="yowan-admin-resource-table__subline">{row.instructorEmail || '-'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.roleLabel || '-'}</p>
                  </td>
                  <td style={memberColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.memberName || '未紐づき'}</p>
                    <p className="yowan-admin-resource-table__subline">{row.memberEmail || '外部講師'}</p>
                  </td>
                  <td style={specialtiesColumnStyle}>{row.specialties || '-'}</td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${activeUi.tone}`}>
                      {activeUi.label}
                    </span>
                  </td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${publicUi.tone}`}>
                      {publicUi.label}
                    </span>
                  </td>
                  <td style={updatedAtColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions" style={actionGroupStyle}>
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  {loadError || '表示できる講師がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={8}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminInstructorsListPage;
